/**
 * Normalize campus/term strings for comparison (matches CMRP sidebar / URL state usage).
 * @param {string} value
 * @returns {string}
 */
function normalizeCmrpKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '');
}

/**
 * Resolve campus rental grace period and term rental refund date from window.courseData.
 * Uses ddcs_campus_entity.rental_refund_grace_period and ddcs_term.rental_refund_date.
 *
 * @param {string} campusName - Selected campus display name
 * @param {string} termName - Selected term display name (or code)
 * @param {string} [termId] - DDCS term_id when available (preferred)
 * @returns {{ _rental_refund_grace_period: string, _rental_refund_date: string }}
 */
function getCmrpRentalCampusTermProperties(campusName, termName, termId) {
  /** @type {{ _rental_refund_grace_period: string, _rental_refund_date: string }} */
  const empty = { _rental_refund_grace_period: '', _rental_refund_date: '' };
  // @ts-ignore
  const cd = typeof window !== 'undefined' ? window.courseData : null;
  if (!cd || !Array.isArray(cd.ddcs_campus_entity) || !Array.isArray(cd.ddcs_term)) {
    return empty;
  }

  const norm = normalizeCmrpKey;
  /** @type {any} */
  const campus = cd.ddcs_campus_entity.find(
    /** @param {any} c */ c => norm(c.name) === norm(campusName)
  );

  /** @type {any} */
  let term = null;
  const tid = termId != null && String(termId).trim() !== '' ? String(termId).trim() : '';
  if (tid) {
    term = cd.ddcs_term.find(
      /** @param {any} t */ t =>
        String(t.term_id) === tid || norm(t.term_id) === norm(tid)
    );
  }
  if (!term && termName) {
    term = cd.ddcs_term.find(
      /** @param {any} t */ t =>
        norm(t.name) === norm(termName) ||
        (t.term != null && norm(t.term) === norm(termName))
    );
  }

  const grace = campus?.rental_refund_grace_period;
  const refundDate = term?.rental_refund_date;

  return {
    _rental_refund_grace_period: grace != null && String(grace) !== '' ? String(grace) : '',
    _rental_refund_date:
      refundDate != null && String(refundDate) !== '' ? String(refundDate) : '',
  };
}

/**
 * Parse a rental due date from ISO (YYYY-MM-DD) or US (MM/DD/YYYY or MM/DD/YY).
 * Suggested / snapshot selections keep the metafield ISO value; in-card checkboxes
 * may already be formatted with slashes.
 *
 * @param {string|null|undefined} raw
 * @returns {{ iso: string, display: string } | null}
 */
function parseRentalDueDate(raw) {
  const value = String(raw || '').trim();
  if (!value) return null;

  let year = '';
  let month = '';
  let day = '';

  if (value.includes('-')) {
    const parts = value.split('-');
    if (parts.length < 3) return null;
    year = parts[0];
    month = parts[1];
    day = parts[2];
  } else if (value.includes('/')) {
    const parts = value.split('/');
    if (parts.length < 3) return null;
    month = parts[0];
    day = parts[1];
    year = parts[2];
    if (year.length === 2) year = `20${year}`;
  } else {
    return null;
  }

  if (!year || !month || !day) return null;

  const iso = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  const display = `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year.slice(-2)}`;
  return { iso, display };
}

/**
 * @param {any} gid
 * @returns {string|null}
 */
function cmrpExtractVariantId(gid) {
  if (gid == null || gid === '') return null;
  if (typeof gid !== 'string') return String(gid);
  const parts = gid.split('/');
  return parts[parts.length - 1] || null;
}

function cmrpIsTruthyFlag(value) {
  return value === true || String(value || '').toLowerCase() === 'true';
}

/**
 * Unified CMRP folds CEB variants onto the TXT parent. Prefer the checkbox /
 * variant signals the UI already used; do not rely only on the print parent's
 * variant_digital_status map (it does not include merged digital variant ids).
 * @param {any} selectedItem
 * @param {any} product
 * @returns {boolean}
 */
function cmrpSelectedItemIsDigital(selectedItem, product) {
  if (cmrpIsTruthyFlag(selectedItem?.isDigital)) return true;

  const digitalFn = window.ProductSystem?.getVariantIsDigitalString;
  if (typeof digitalFn === 'function') {
    if (digitalFn(selectedItem?.variant, product) === 'true') return true;
  }

  const variantNumericId = cmrpExtractVariantId(selectedItem?.variant?.id);
  const digitalStatus =
    variantNumericId && product?.metafields?.custom?.variant_digital_status
      ? product.metafields.custom.variant_digital_status[variantNumericId]
      : undefined;
  return cmrpIsTruthyFlag(digitalStatus);
}

/**
 * @param {any} selectedItem
 * @param {any} product
 * @returns {string}
 */
function cmrpSelectedItemPublisherCode(selectedItem, product) {
  const fromItem = String(selectedItem?.publisherCode || '').trim();
  if (fromItem && fromItem.toLowerCase() !== 'null') return fromItem;

  const publisherFn = window.ProductSystem?.getVariantPublisherCode;
  if (typeof publisherFn === 'function') {
    const fromVariant = String(publisherFn(selectedItem?.variant, product) || '').trim();
    if (fromVariant) return fromVariant;
  }

  return String(
    product?.metafields?.course_materials?.publisher_code ||
      product?.metafields?.publisher_code ||
      ''
  ).trim();
}

/**
 * Flatten Shopify cart/add error fields. Description is sometimes an object.
 * @param {any} error
 * @returns {string}
 */
function cmrpErrorText(error) {
  const chunks = [];
  const push = value => {
    if (value == null || value === '') return;
    if (typeof value === 'string' || typeof value === 'number') {
      chunks.push(String(value));
      return;
    }
    if (typeof value === 'object') {
      try {
        chunks.push(JSON.stringify(value));
      } catch (_err) {
        /* ignore */
      }
    }
  };
  push(error?.message);
  push(error?.description);
  push(error?.payload?.description);
  push(error?.payload?.message);
  push(error?.payload?.errors);
  try {
    push(String(error));
  } catch (_err) {
    /* ignore */
  }
  return chunks.join(' ');
}

/**
 * @param {any} error
 * @returns {boolean}
 */
function cmrpIsCartStockError(error) {
  const status = Number(error?.status ?? error?.payload?.status);
  if (status === 422 || status === 409) return true;
  const msg = cmrpErrorText(error).toLowerCase();
  return /sold[\s\-_]*out|not enough items|not enough inventory|no longer available|cannot be added/.test(
    msg
  );
}

function cmrpShouldRecoverPerItem(error) {
  if (cmrpIsCartStockError(error)) return true;
  return Number(error?.status ?? error?.payload?.status) === 500;
}

/**
 * CMRP-only /cart/add.js. Does not use window.addCartItems (shared with PDP).
 * @param {Array<{id: any, quantity: any, properties?: Record<string, string>}>} cartLines
 * @returns {Promise<any>}
 */
async function cmrpAddCartLines(cartLines) {
  if (!Array.isArray(cartLines) || cartLines.length === 0) {
    return [];
  }
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: cartLines }),
  });
  const payload = await response.json().catch(() => ({}));
  const status = Number(payload?.status) || response.status;
  if (!response.ok || status >= 400) {
    const description = payload?.description;
    const message =
      (typeof description === 'string' && description) ||
      (typeof payload?.message === 'string' && payload.message) ||
      (description != null ? JSON.stringify(description) : '') ||
      `Failed to add items to cart (HTTP ${status})`;
    const error = new Error(message);
    error.status = status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

function cmrpIsDigitalFeeLine(line) {
  return String(line?.properties?._digital_delivery_fee || '') === 'true';
}

/**
 * SOLID Product Handler - Main facade for product system
 */
class SOLIDProductHandler {
  /**
   * @param {any} options - Configuration options
   */
  constructor(options = {}) {
    this.products = options.products || [];
    this.ddcsSection = options.ddcsSection || [];

    this.variantDetector =
      options.variantDetector ||
      // @ts-ignore
      new window.ProductSystem.ShopifyVariantDetector();
    this.infoExtractor =
      // @ts-ignore
      options.infoExtractor || new window.ProductSystem.ShopifyInfoExtractor();
    this.priceCalculator =
      options.priceCalculator ||
      // @ts-ignore
      new window.ProductSystem.ShopifyPriceCalculator();
    this.htmlRenderer =
      options.htmlRenderer ||
      // @ts-ignore
      new window.ProductSystem.ShopifyHTMLRenderer(
        this.variantDetector,
        this.priceCalculator
      );
    this.adoptionManager =
      options.adoptionManager ||
      // @ts-ignore
      new window.ProductSystem.AdoptionStatusManager();

    // Bind methods for callbacks
    this.addSelectedProductsToCart = this.addSelectedProductsToCart.bind(this);
    this.handleNotifyMe = this.handleNotifyMe.bind(this);
  }

  // Variant detection
  /**
   * @param {Product} product - Product object
   * @returns {boolean} Has digital variants
   */
  hasDigitalVariants(product) {
    return this.variantDetector.hasDigitalVariants(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {boolean} Has print variants
   */
  hasPrintVariants(product) {
    return this.variantDetector.hasPrintVariants(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {boolean} Has rental variants
   */
  hasRentalVariants(product) {
    return this.variantDetector.hasRentalVariants(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {boolean} Has buy variants
   */
  hasBuyVariants(product) {
    return this.variantDetector.hasBuyVariants(product);
  }

  // Information extraction
  /**
   * @param {Product} product - Product object
   * @returns {string|null} Author name
   */
  extractAuthorName(product) {
    return this.infoExtractor.extractAuthorName(product);
  }

  /**
   * @param {string} title - Product title
   * @returns {string|null} Edition
   */
  extractEditionFromTitle(title) {
    return this.infoExtractor.extractEditionFromTitle(title);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} ISBN
   */
  extractISBNFromProduct(product) {
    return this.infoExtractor.extractISBNFromProduct(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} Publisher name
   */
  extractPublisherName(product) {
    return this.infoExtractor.extractPublisherName(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} Publisher name
   */
  extractPublisher(product) {
    return this.infoExtractor.extractPublisher(product);
  }
  /**
   * @param {Product} product - Product object
   * @returns {string|null} Publisher name
   */
  extractFormat(product) {
    return this.infoExtractor.extractFormat(product);
  }
  /**
   * @param {Product} product - Product object
   * @returns {string|null} Publisher name
   */
  extractCopyRight(product) {
    return this.infoExtractor.extractCopyRight(product);
  }
  /**
   * @param {Product} product - Product object
   * @returns {string|null} Publisher name
   */
  extractPublication(product) {
    return this.infoExtractor.extractPublication(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} Published date
   */
  extractPublishedDate(product) {
    return this.infoExtractor.extractPublishedDate(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} Publisher code
   */
  extractPublisherCode(product) {
    return this.infoExtractor.extractPublisherCode(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} MTC Title ID
   */
  extractMTCTitleId(product) {
    return this.infoExtractor.extractMTCTitleId(product);
  }

  // Price calculation
  /**
   * @param {Product} product - Product object
   * @param {string} format - Format
   * @returns {string|null} Lowest price
   */
  getLowestPriceForFormat(product, format) {
    return this.priceCalculator.getLowestPriceForFormat(product, format);
  }

  /**
   * @param {Product} product - Product object
   * @returns {Object|null} Price range
   */
  calculatePriceRange(product) {
    return this.priceCalculator.calculatePriceRange(product);
  }

  // HTML generation
  /**
   * @param {Product} product - Product object
   * @returns {string} HTML
   */
  generateFormatButtons(product) {
    return this.htmlRenderer.generateFormatButtons(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string} HTML
   * @param {any} type
   */
  async generateInitialPurchaseOptions(type,product,sectionData) {
    return await this.htmlRenderer.generatePurchaseOptions(type,product,sectionData);
  }
  /**
   * @param {Product} product - Product object
   * @returns {string} HTML
   */
  getVariantUniques(product) {
    return this.htmlRenderer._getFormats(product);
  }

  /**
   * @param {Product} product - Product object
   * @returns {string} HTML
   */
  async generatePurchaseOptions(type,product) {
    return await this.htmlRenderer.generatePurchaseOptions(type,product);
  }

  // Adoption status
  /**
   * @param {string} note - Note text
   * @returns {StatusInfo} Status info
   */
  getAdoptionStatusInfo(note) {
    return this.adoptionManager.getStatusInfo(note);
  }

  /**
   * @param {Filter} filter - Filter object
   * @param {Section} section - Section object
   * @param {string} courseCode - Course code (unused but kept for compatibility)
   * @param {string} note - Note text
   * @param {Object} statusInfo - Status info object (unused but kept for compatibility)
   * @returns {string} HTML
   */
  // @ts-ignore
  createAdoptionCardHTML(filter, section, courseCode, note, statusInfo) {
    return this.adoptionManager.createAdoptionCard(filter, section, note).html;
  }

  /**
   * @param {Array<Object>} selectedProducts
   * @returns {Promise<{ cartLines: Array<Object> }>}
   */
  async #buildCartLinesFromSelected(selectedProducts) {
    const cartLines = [];
    let feeQuantity = 0;
    let rentalHoldStarted = false;

    for (const item of selectedProducts) {
      /** @type {any} */
      const selectedItem = item;

      // @ts-ignore
      const product = window.courseData.products_Materials.find(p => p.id == selectedItem.productId);

      let isDigital = cmrpSelectedItemIsDigital(selectedItem, product);
      let publisherCode = cmrpSelectedItemPublisherCode(selectedItem, product);

      const variantMetafields = Array.isArray(selectedItem.variant?.metafields)
        ? selectedItem.variant.metafields
        : [];
      const isRental =
        variantMetafields.find(
          mf => mf.is_rental !== undefined && mf.namespace === 'cm_rental'
        )?.is_rental ?? false;

      const rentalDue = parseRentalDueDate(selectedItem.bookRentDueDate);

      const variantTitle = String(selectedItem.variant?.title || '').toLowerCase();
      let processing_price_nonreturn =
        variantMetafields.find(
          mf =>
            mf.processing_price_nonreturn !== undefined &&
            mf.namespace === 'cm_rental'
        )?.processing_price_nonreturn ?? 0;
      let non_return_charge = 0;
      if (variantTitle.includes('new')) {
        non_return_charge =
          variantMetafields.find(
            mf =>
              mf.new_price_nonreturn !== undefined && mf.namespace === 'cm_rental'
          )?.new_price_nonreturn ?? 0;
      }
      if (variantTitle.includes('used')) {
        non_return_charge =
          variantMetafields.find(
            mf =>
              mf.used_price_nonreturn !== undefined && mf.namespace === 'cm_rental'
          )?.used_price_nonreturn ?? 0;
      }
      let properties = {};
      if (item.rentalOnlySku === 'true') {
        properties['_CM_Rental_Only_SKU'] = item.rentalOnlySku;
      }
      properties['_Course_Material'] = 'true';
      properties['Course'] = [
        selectedItem.campus,
        selectedItem.term,
        selectedItem.division,
        selectedItem.department,
        selectedItem.course,
        selectedItem.section,
      ].filter(Boolean).join(' > ');
      properties['_Format'] = selectedItem.format || '';
      properties['_Type'] = selectedItem.type || '';
      properties['Edition'] = selectedItem.bookEdition || '';
      properties['_Campus'] = selectedItem.campus || '';
      properties['_Term'] = selectedItem.term || '';
      properties['_Division'] = selectedItem.division || '';
      properties['_Department'] = selectedItem.department || '';
      properties['_Course'] = selectedItem.course || '';
      properties['_Section'] = selectedItem.section || '';
      properties['ISBN'] = selectedItem.bookISBN || '';

      const purchaseTypeStr = String(selectedItem.purchaseType || '');
      const typeLabelStr = String(selectedItem.type || '');
      const isBuyType = /buy/i.test(purchaseTypeStr) || /buy/i.test(typeLabelStr);

      if (!isBuyType) {
        const rentalRefundFromCourseData = getCmrpRentalCampusTermProperties(
          selectedItem.campus || '',
          selectedItem.term || '',
          selectedItem.termId || ''
        );
        properties['_rental_refund_grace_period'] = rentalRefundFromCourseData._rental_refund_grace_period;
        properties['_rental_refund_date'] = rentalRefundFromCourseData._rental_refund_date;
      }

      if (isDigital) {
        properties['_is_digital'] = 'true';
        properties['_publisher_code'] = publisherCode || '';

        if (await this.#shouldApplyFeeForPublisher(publisherCode || 'default')) {
          properties['_requires_digital_delivery_fee'] = 'yes';
          feeQuantity += 1;
        } else {
          properties['_requires_digital_delivery_fee'] = 'no';
        }

        if (selectedItem.subscriptionLength && selectedItem.subscriptionLength !== ''  && selectedItem.subscriptionLength !== '999' && selectedItem.subscriptionLength !== '0') {
          properties['Rental Duration'] = selectedItem.subscriptionLength + " days";
        }
      }

      if (isRental) {
        if (!rentalHoldStarted) {
          window.rentalCollateralManager?.notifyRentalAddStarting?.();
          rentalHoldStarted = true;
        }
        properties['_rental_flag'] = true;
        properties['_non_return_processing_fee'] = processing_price_nonreturn;
        properties['_non_return_charge'] = non_return_charge;

        const rentalCampusTerm = getCmrpRentalCampusTermProperties(
          selectedItem.campus || '',
          selectedItem.term || '',
          selectedItem.termId || ''
        );
        properties['_rental_grace_period'] = rentalCampusTerm._rental_grace_period;
        properties['_rental_refund_date'] = rentalCampusTerm._rental_refund_date;

        if (rentalDue) {
          properties['_rental_expiration_date'] = rentalDue.iso;
          properties['Rent Due Date'] = rentalDue.display;
        }
      }

      if (selectedItem.departmentData) {
        properties['_department'] = selectedItem.departmentData;
      }
      if (selectedItem.subDepartment) {
        properties['_sub_department'] = selectedItem.subDepartment;
      }
      if (selectedItem.classds) {
        properties['_class'] = selectedItem.classds;
      }
      if (selectedItem.subClass) {
        properties['_sub_class'] = selectedItem.subClass;
      }
      cartLines.push({
        id: selectedItem.variant.id.split('/').pop(),
        quantity: 1,
        properties,
      });
    }

    if (feeQuantity > 0 && typeof window.buildDigitalFeeCartLine === 'function') {
      const feeLine = await window.buildDigitalFeeCartLine(feeQuantity);
      if (feeLine) cartLines.push(feeLine);
    }

    return { cartLines };
  }

  /**
   * Bulk-add first. On inventory 422, add remaining items one by one and return OOS rows.
   * @param {Array<Object>} selectedProducts
   * @returns {Promise<Array<Object>>}
   */
  async #addCartLinesHandlingStock(selectedProducts) {
    const { cartLines } = await this.#buildCartLinesFromSelected(selectedProducts);
    try {
      await cmrpAddCartLines(cartLines);
      return [];
    } catch (error) {
      if (!cmrpShouldRecoverPerItem(error)) throw error;
    }

    /** @type {Array<Object>} */
    const oosItems = [];
    /** @type {Array<Object>} */
    const addedItems = [];
    for (const item of selectedProducts) {
      const one = await this.#buildCartLinesFromSelected([item]);
      const productLines = one.cartLines.filter(line => !cmrpIsDigitalFeeLine(line));
      if (productLines.length === 0) {
        oosItems.push(item);
        continue;
      }
      try {
        await cmrpAddCartLines(productLines);
        addedItems.push(item);
      } catch (itemError) {
        if (cmrpShouldRecoverPerItem(itemError)) {
          oosItems.push(item);
        } else {
          throw itemError;
        }
      }
    }

    if (addedItems.length > 0) {
      const addedLines = await this.#buildCartLinesFromSelected(addedItems);
      const feeLines = addedLines.cartLines.filter(line => cmrpIsDigitalFeeLine(line));
      if (feeLines.length > 0) {
        try {
          await cmrpAddCartLines(feeLines);
        } catch {
          /* Fees are never treated as OOS. */
        }
      }
    }

    return oosItems;
  }

  // Cart functionality
  async addSelectedProductsToCart() {
    /** @type {Array<Object>} */
    const selectedProducts = [];
    const checkboxes = document.querySelectorAll('.purchase-checkbox:checked');
    
    const extractVariantId = cmrpExtractVariantId;
    
    // Also get products from SelectionManager (includes synced suggested materials)
    const selectionManagerProducts = window.courseMaterialsPage?.components?.selectionManager?.selectedProducts || [];

    const viewChoice = document.querySelectorAll('.view-more-btn-choice.expanded');

    checkboxes.forEach(checkbox => {
      /** @type {HTMLInputElement} */
      const checkboxElement = /** @type {HTMLInputElement} */ (checkbox);
      const courseCard = checkboxElement.closest('.course-card');
      // Checkbox has no course data (html-renderer doesn't set it). Fall back to course card dataset from product-display-manager.
      const campus = checkboxElement.dataset.campus || courseCard?.dataset?.campus || '';
      const term = checkboxElement.dataset.term || courseCard?.dataset?.term || '';
      const termId =
        checkboxElement.dataset.termId || courseCard?.dataset?.termId || '';
      const division = checkboxElement.dataset.division || courseCard?.dataset?.division || '';
      const department = checkboxElement.dataset.department || courseCard?.dataset?.department || '';
      const course = checkboxElement.dataset.course || courseCard?.dataset?.course || '';
      const section = checkboxElement.dataset.section || courseCard?.dataset?.section || '';
      const productId = checkboxElement.dataset.productId;
      const purchaseType = checkboxElement.dataset.purchaseType;
      const variantId = checkboxElement.dataset.variantId;
      const productTitle = checkboxElement.dataset.productTitle;
      const bookISBN = checkboxElement.dataset.bookIsbn;
      const bookEdition = checkboxElement.dataset.bookEdition;
      const bookCopyright = checkboxElement.dataset.bookCopyright;
      const bookPublisher = checkboxElement.dataset.bookPublisher;
      const bookRentDueDate = checkboxElement.dataset.bookRentDueDate;

      const format = checkboxElement.dataset.format;
      const type = checkboxElement.dataset.type;

      const departmentData = checkboxElement.dataset.departmentTaxonomy;
      const subDepartment = checkboxElement.dataset.subDepartment;
      const classds = checkboxElement.dataset.class;
      const subClass = checkboxElement.dataset.subClass;

      const rentalOnlySku= checkboxElement.dataset.rentalOnlySku;

      const subscriptionLength = checkboxElement.dataset.subscriptionLength;

      const materialItem = checkboxElement.closest('.material-item');
      /** @type {HTMLElement|null} */
      const materialElement = /** @type {HTMLElement|null} */ (materialItem);
      const selectedFormat = materialElement
        ? this.getSelectedFormat(materialElement)
        : null;

      // Check if this is a simple product (determined by html-renderer based on variant data)
      const formatBtns = materialElement?.querySelectorAll('.format-btn') || [];
      const isSimple = formatBtns.length === 0;

      // Simple products only need productId & purchaseType, complex products also need selectedFormat
      const hasRequiredData = isSimple
        ? productId && purchaseType
        : selectedFormat && productId && purchaseType;

      if (hasRequiredData) {
        // @ts-ignore
        const product = window.courseData.products_Materials.find(
          /** @param {any} p */ p => p.id == productId
        );

        // For simple products, use variant ID directly. For complex products, use format-based lookup
        const variant = isSimple
          ? product?.variants.find(/** @param {any} v */ v => v.id == variantId)
          : product?.variants.find(
              /** @param {any} v */ v => v.id == variantId
            // @ts-ignore
            ) || this.findVariant(productId, selectedFormat, purchaseType);

        if (variant) {
          selectedProducts.push({
            campus,
            term,
            termId,
            division,
            department,
            course,
            section,
            productId,
            productTitle,
            bookISBN,
            bookEdition,
            bookCopyright,
            bookPublisher,
            bookRentDueDate,
            selectedFormat: selectedFormat || 'simple', // Use 'simple' as fallback
            purchaseType,
            variant,
            price: parseFloat(variant.price),
            departmentData,
            subDepartment,
            classds,
            subClass,
            rentalOnlySku,
            subscriptionLength,
            format,
            type,
            isDigital: checkboxElement.dataset.isDigital,
            publisherCode: checkboxElement.dataset.publisherCode || '',
          });
        }
      }
    });
    
    // Add products from SelectionManager (suggested materials and other selections)
    // This includes products selected from the React component
    selectionManagerProducts.forEach(item => {
      // Skip if already added from checkboxes (by variant ID)
      const variantId = extractVariantId(item.variant?.id);
      const alreadyAdded = selectedProducts.some(sp => {
        const spVariantId = extractVariantId(sp.variant?.id);
        return spVariantId === variantId;
      });
      
      if (!alreadyAdded && item.variant) {
        // Get additional data from the product if available
        const product = window.courseData?.products_Materials?.find(
          p => p.id == item.productId
        );

        // Prefer props already on the selection (session restore / updateSelectedProducts);
        // fall back to live checkbox/DOM when present.
        const materialItem = document.querySelector(`[data-product-id="${item.productId}"]`);
        const checkbox = materialItem?.querySelector('.purchase-checkbox:checked');
        const courseCardSm =
          materialItem?.closest('.course-card') ?? checkbox?.closest('.course-card');

        // If restore left a stub `{ id }` only, resolve the full variant by exact id.
        let variant = item.variant;
        const hasMetafields = Array.isArray(variant?.metafields);
        if (!hasMetafields && item.productId && variant?.id != null) {
          const targetVid = extractVariantId(variant.id);
          const full = (product?.variants || []).find(
            v => extractVariantId(v.id) === targetVid
          );
          if (full) {
            variant = full;
          }
        }

        const itemIsDigital =
          item.isDigital === true || String(item.isDigital).toLowerCase() === 'true';

        selectedProducts.push({
          campus:
            item.campus ||
            checkbox?.dataset.campus ||
            courseCardSm?.dataset?.campus ||
            '',
          term:
            item.term || checkbox?.dataset.term || courseCardSm?.dataset?.term || '',
          termId:
            item.termId ||
            checkbox?.dataset.termId ||
            courseCardSm?.dataset?.termId ||
            '',
          division:
            item.division ||
            checkbox?.dataset.division ||
            courseCardSm?.dataset?.division ||
            '',
          department:
            item.department ||
            checkbox?.dataset.department ||
            courseCardSm?.dataset?.department ||
            '',
          course:
            item.course ||
            checkbox?.dataset.course ||
            courseCardSm?.dataset?.course ||
            '',
          section:
            item.section ||
            checkbox?.dataset.section ||
            courseCardSm?.dataset?.section ||
            '',
          productId: item.productId,
          productTitle: item.productTitle,
          bookISBN:
            item.bookISBN ||
            product?.metafields?.isbn_ean ||
            product?.metafields?.course_materials?.isbn_ean ||
            '',
          bookEdition:
            item.bookEdition ||
            product?.metafields?.edition ||
            product?.metafields?.course_materials?.edition ||
            '',
          bookCopyright:
            item.bookCopyright ||
            product?.metafields?.copyright_year ||
            product?.metafields?.course_materials?.copyright_year ||
            '',
          bookPublisher:
            item.bookPublisher ||
            product?.metafields?.publisher_name ||
            product?.metafields?.course_materials?.publisher_name ||
            '',
          bookRentDueDate:
            item.bookRentDueDate || checkbox?.dataset.bookRentDueDate || '',
          selectedFormat: item.selectedFormat || 'simple',
          purchaseType: item.purchaseType || 'Buy',
          variant,
          price: item.price || parseFloat(variant?.price) || 0,
          departmentData:
            item.departmentData || checkbox?.dataset.departmentTaxonomy || '',
          subDepartment:
            item.subDepartment || checkbox?.dataset.subDepartment || '',
          classds: item.classds || checkbox?.dataset.class || '',
          subClass: item.subClass || checkbox?.dataset.subClass || '',
          rentalOnlySku:
            item.rentalOnlySku || checkbox?.dataset.rentalOnlySku || '',
          subscriptionLength:
            item.subscriptionLength || checkbox?.dataset.subscriptionLength || '',
          format: item.format || checkbox?.dataset.format || '',
          type: item.type || checkbox?.dataset.type || '',
          isDigital: itemIsDigital,
          publisherCode: item.publisherCode || '',
        });
      }
    });

    if (selectedProducts.length === 0) {
      alert('Please select some products first.');
      return;
    }

    let oosItems = [];
    try {
      oosItems = await this.#addCartLinesHandlingStock(selectedProducts);
    } catch (error) {
      console.error('Error adding products to cart:', error);
      if (cmrpIsCartStockError(error) || /sold[\s\-_]*out/i.test(String(error?.message || error || ''))) {
        oosItems = selectedProducts.slice();
      } else {
        alert('Error adding products to cart: ' + error.message);
        return;
      }
    }

    try {
      // Uncheck first; clearSelections() syncs .selected / notices (no change event).
      checkboxes.forEach(cb => {
        /** @type {HTMLInputElement} */
        const checkbox = /** @type {HTMLInputElement} */ (cb);
        checkbox.checked = false;
      });
     
      if (viewChoice && viewChoice.length > 0) {         
        viewChoice.forEach(viewMoreBtnChoice => {         
          viewMoreBtnChoice.click();
        });
      }
      

      // Clear SelectionManager selections
      if (window.courseMaterialsPage?.components?.selectionManager) {
        window.courseMaterialsPage.components.selectionManager.clearSelections();
      }

      // Clear React component selections (suggested + bundle)
      ['selectedSuggestedMaterials', 'selectedBundleMaterials'].forEach(storeName => {
        const listenersName = `${storeName}Listeners`;
        if (typeof window !== 'undefined' && window[storeName]) {
          window[storeName] = [];
          if (window[listenersName]) {
            window[listenersName].forEach(listener => {
              try {
                listener();
              } catch (e) {
                console.error('Error in selection change listener:', e);
              }
            });
          }
        }
      });

      const selectionBar = document.querySelector('selection-bar-component');
      if (selectionBar) {
        /** @type {any} */
        const selectionBarElement = selectionBar;
        // Use the existing initializeState method to properly reset the selection bar
        if (selectionBarElement.initializeState) {
          selectionBarElement.initializeState();
        } else {
          // Fallback to manual reset
          selectionBarElement.currentSelectedCount = 0;
          selectionBarElement.currentSelectedTotal = '0.00';
          selectionBarElement.updateVisibility();
        }
      }

      // Get cart data and dispatch update
      const cartData = await fetch('/cart.js').then(r => r.json());
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartData,
            sourceId: 'course-materials',
            data: {
              itemCount: cartData.item_count,
              source: 'course-materials',
            },
          },
        })
      );

      // Check if we're on course materials page - if so, don't open cart drawer
      const isCourseMaterialsPage = document.body.classList.contains('course-materials-page') || 
                                   window.location.pathname.includes('courses-materials');
      
      if (isCourseMaterialsPage) {
        // For course materials page, just redirect to cart without opening drawer
        //window.location.href = '/cart';
      } else {
        // For other pages, open cart drawer as usual
        const cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer) {
          /** @type {any} */
          const cartDrawerElement = cartDrawer;
          if (cartDrawerElement.open) {
            cartDrawerElement.open();
          } else {
            //window.location.href = '/cart';
          }
        } else {
          //window.location.href = '/cart';
        }
      }

      if (
        oosItems.length > 0 &&
        typeof window.CmrpOosModal?.open === 'function'
      ) {
        window.CmrpOosModal.open(oosItems);
      }
    } catch (error) {
      console.error('Error adding products to cart:', error);
    }
  }

  /**
   * @param {HTMLElement} materialItem - Material item element
   * @returns {string|null} Selected format
   */
  getSelectedFormat(materialItem) {
    const selectedBtn = materialItem?.querySelector('.format-btn.selected');
    return selectedBtn ? selectedBtn.getAttribute('data-format') : null;
  }

  /**
   * @param {string} productId - Product ID
   * @param {string} selectedFormat - Selected format
   * @param {string} purchaseType - Purchase type
   * @returns {Object} Variant object
   */
  findVariant(productId, selectedFormat, purchaseType) {
    const product = window.courseData.products_Materials.find(
      /** @param {any} p */ p => p.id == productId
    );
    if (!product) throw new Error('Product not found');

    const variant = this.priceCalculator.getVariantByOptions(
      product,
      selectedFormat,
      purchaseType
    );

    if (!variant) {
      throw new Error(
        `Variant not found for ${selectedFormat}/${purchaseType}`
      );
    }
    return variant;
  }
  async addNewAttributeForFee(cart, VARIANT_ID , value) {
    if (!VARIANT_ID) {
      return;
    }
    try {
      // 2. Find the line item by variant_id or line item key
      const lineItem = cart.items.find(item => item.variant_id === parseInt(VARIANT_ID));

      if (!lineItem) {
        console.error('Line item not found');
        return;
      }

      // 3. Merge existing properties with the new one
      const newProperties = {
        ...lineItem.properties, // existing properties
        _requires_digital_delivery_fee: value // new property
      };

      // 4. Update the line item with merged properties
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: lineItem.key, // use the line item key for precise targeting
          properties: newProperties
        })
      });
      if (response.ok) {
        return true;
      } else {
        const error = await response.json().catch(() => ({}));
        console.error('Failed to remove collateral product:', error);
      }
    } catch (error) {
      console.error('Error removing collateral product:', error);
    }
  }
  /**
   * Add digital delivery fees for selected digital items
   * @param {Array} digitalItems - Array of digital items
   */
  async #addDigitalDeliveryFees(digitalItems) {
    // 1. Fetch the cart
    const responsedata = await fetch('/cart.js');
    const cart = await responsedata.json();
    // Group digital items by publisher to determine fee logic
    //const publisherGroups = this.#groupDigitalItemsByPublisher(digitalItems);
    
    for (const item of digitalItems) {
      const variants = Array.isArray(item?.variants) ? item.variants : [];
      const firstVariant = variants[0];
      const metafields = Array.isArray(firstVariant?.metafields)
        ? firstVariant.metafields
        : [];
      const foundPublisherCode = metafields.find(
        metafield =>
          metafield?.namespace === 'course_materials' && metafield.publisher_code
      );

      const publisherCode =
        foundPublisherCode?.publisher_code || item.publisherCode || 'default';
      const variantNumericId = firstVariant?.id
        ? String(firstVariant.id).split('/').pop()
        : '';
      if (!variantNumericId) continue;

      if (await this.#shouldApplyFeeForPublisher(publisherCode)) {
        await this.addNewAttributeForFee(cart, variantNumericId, 'yes')
        await this.#addFeeForPublisher(1, publisherCode);
      } else {
        await this.addNewAttributeForFee(cart, variantNumericId, 'no')
      }
    }
  }

  /**
   * Group digital items by publisher code
   * @param {Array} digitalItems - Digital items
   * @returns {Object} Grouped items by publisher
   */
  #groupDigitalItemsByPublisher(digitalItems) {
    const groups = {};
    
    digitalItems.forEach(item => {
      const foundPublisherCode = item.variants[0].metafields.find(item => item.namespace === "course_materials" && item.publisher_code);
      const publisherCode = foundPublisherCode ? foundPublisherCode.publisher_code : 'default'
      // @ts-ignore
      if (!groups[publisherCode]) {
        // @ts-ignore
        groups[publisherCode] = [];
      }
      // @ts-ignore
      groups[publisherCode].push(item);
    });
    
    return groups;
  }

  /**
   * Check if fee should be applied for publisher
   * @param {Object} config - Digital fee config
   * @param {string} publisherCode - Publisher code
   * @returns {boolean}
   */
  async #shouldApplyFeeForPublisher(publisherCode) {
    if (typeof window.shouldApplyDigitalFeeForPublisher === 'function') {
      try {
        return await window.shouldApplyDigitalFeeForPublisher(publisherCode);
      } catch (error) {
        console.error(error);
        return false;
      }
    }
    const api_url = window.digital_product_config?.api_url ?? ''
    const store_number = window.digital_product_config?.store_number ?? ''

    if(api_url && store_number) {
      try {
        const apiResponse = await fetch(`${api_url}?storeNumber=${store_number}&downloadprovidercode=${publisherCode}`)
        const result = await apiResponse.json();
        if(result && result.length > 0 && result[0].isApplied) {
          return true;
        } else {
          return false
        }
      } catch (error) {
        console.error(error);
      }
      
    } else {
      // Digital fee config not found
    }
  }
  async fetchMetafields(variantId) {
    if (typeof window !== 'undefined' && window.fetchMetafieldsOnce) {
      return window.fetchMetafieldsOnce(variantId);
    }

    const proxyPath = '/apps/shopifyData/getMetafields';
    const url = `${proxyPath}?query=gid://shopify/ProductVariant/${variantId}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to fetch Metafields');
      }
    } catch (error) {
      console.error('Error fetching Metafields:', error);
      throw error;
    }
  }
  /**
   * Add fee for specific publisher
   * @param {number} quantity - Quantity of items
   */
  // @ts-ignore
  async #addFeeForPublisher(quantity, feePublisherCode) {
    try {
      
      const feeProductHandle = window.digital_product_config?.product_handle ?? '';
      
      // Get the fee product to find its variant ID
      const productResponse = await fetch(`/products/${feeProductHandle}.js`);
      if (!productResponse.ok) {
        console.error('Digital delivery fee product not found');
        return;
      }
      
      const feeProduct = await productResponse.json();
      const feeVariantId = feeProduct.variants[0]?.id;
      
      if (!feeVariantId) {
        console.error('No variant found for digital delivery fee product');
        return;
      }
      const metafields = await this.fetchMetafields(feeVariantId)
      const getMetafieldValue = (key) => {
          const metafield = metafields.find(m => m.key === key);
          return metafield ? metafield.value : ''; 
      };
      const feeSubClass = getMetafieldValue('sub_class');
      // Add the fee product to cart
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: feeVariantId,
          quantity: quantity,
          properties: {
            '_digital_delivery_fee': 'true',
            '_is_digital': 'true',
            '_fee_type': 'online_store',
            '_department': getMetafieldValue('department'), 
            '_sub_department': getMetafieldValue('sub_department'), 
            '_class': getMetafieldValue('class'), 
            '_sub_class': feeSubClass
          }
        })
      });
    } catch (error) {
      console.error('Failed to add digital delivery fee:', error);
    }
  }

  /**
   * @param {string} variantId - Variant ID
   * @param {string} productTitle - Product title
   * @param {string} purchaseType - Purchase type
   */
  handleNotifyMe(variantId, productTitle, purchaseType) {
    // @ts-ignore
    if (typeof window.openNotifyMeDialog === 'function') {
      // @ts-ignore
      window.openNotifyMeDialog(variantId, productTitle, purchaseType);
    } else {
      alert(
        `We'll notify you when "${productTitle}" (${purchaseType}) is back in stock!`
      );
    }
  }

  // Factory method
  /**
   * @param {Product[]} products - Products array
   * @param {any[]} ddcsSection - DDCS section array
   * @returns {SOLIDProductHandler} Handler instance
   */
  static createForShopify(products, ddcsSection) {
    return new SOLIDProductHandler({
      products,
      ddcsSection,
      // @ts-ignore
      variantDetector: new window.ProductSystem.ShopifyVariantDetector(),
      // @ts-ignore
      infoExtractor: new window.ProductSystem.ShopifyInfoExtractor(),
      // @ts-ignore
      priceCalculator: new window.ProductSystem.ShopifyPriceCalculator(),
      // @ts-ignore
      htmlRenderer: new window.ProductSystem.ShopifyHTMLRenderer(
        // @ts-ignore
        new window.ProductSystem.ShopifyVariantDetector(),
        // @ts-ignore
        new window.ProductSystem.ShopifyPriceCalculator()
      ),
      // @ts-ignore
      adoptionManager: new window.ProductSystem.AdoptionStatusManager(),
    });
  }
}

// @ts-ignore
window.ProductSystem = window.ProductSystem || {};
// @ts-ignore
window.ProductSystem.SOLIDProductHandler = SOLIDProductHandler;